import "./styles.scss";
import React, { useEffect, useState } from "react";
import { useCurrentUser } from "utils/hooks";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";

const PaymentRedirect = () => {
  const { user, isTherapist, isSubscribed, isAuthenticated } = useCurrentUser();
  const history = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  useEffect(() => {
    const handleRedirect = async () => {
      // If not authenticated, redirect to login
      if (!isAuthenticated || !user) {
        window.location.href = "/login";
        return;
      }

      // If user is already subscribed, go straight to dashboard
      if (isSubscribed) {
        history("/dashboard");
        return;
      }

      // If not a therapist, go to dashboard
      if (!isTherapist) {
        history("/dashboard");
        return;
      }

      try {
        // Track the event with context
        ReactGA.event("navigating_to_stripe_payment", {
          user_id: isAuthenticated ? user?.id : "action_when_unauthenticated",
          context: user?.stripeCustomerId ? "expired_subscription" : "new_user",
        });

        // Ensure we have a valid user with a UUID
        if (!user || !user.id) {
          throw new Error("User not found or missing UUID");
        }

        // Get the redirect URL from the backend
        if (!process.env.REACT_APP_RAW_API_URL) {
          throw new Error("API URL is not configured");
        }

        const response = await fetch(
          `${process.env.REACT_APP_RAW_API_URL}/check-subscription`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId: user.id,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to check subscription");
        }

        const data = await response.json();

        // If we have a redirect URL, use it
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
          return;
        }

        // If user is subscribed, go to dashboard
        if (data.isSubscribed) {
          history("/dashboard");
          return;
        }

        // For new users or non-subscribed users, clear storage and redirect
        if (data.isNewUser || !data.isSubscribed) {
          localStorage.clear();
          const planUrl = `${
            process.env.REACT_APP_STRIPE_PLAN_URL
          }?prefilled_email=${encodeURIComponent(data.email)}`;
          window.location.href = planUrl;
          return;
        }

        // If we get here and haven't exceeded retries, try again
        if (retryCount < MAX_RETRIES) {
          setRetryCount((prev) => prev + 1);
          setTimeout(handleRedirect, RETRY_DELAY);
          return;
        }

        // If we get here, something went wrong
        throw new Error("No valid redirect condition met");
      } catch (error) {
        // Only redirect to login if we've exhausted retries
        if (retryCount >= MAX_RETRIES) {
          window.location.href = "/login";
        }
      } finally {
        setIsLoading(false);
      }
    };

    handleRedirect();
  }, [isTherapist, isSubscribed, user, history, isAuthenticated, retryCount]);

  if (isLoading) {
    return (
      <div className="flex align-center justify-center col not-found-page">
        <h2 className="h2-2 headline">Please wait...</h2>
        {retryCount > 0 && (
          <p>
            Checking subscription status... (Attempt {retryCount}/{MAX_RETRIES})
          </p>
        )}
      </div>
    );
  }

  return null;
};

export default PaymentRedirect;
